import { Lock, Pencil } from "lucide-react";
import { fmtDate } from "./NextUp";

const CELL_STYLES = {
  available: "bg-emerald-500 text-emerald-950 border-emerald-400",
  maybe: "bg-amber-400 text-amber-950 border-amber-300",
  out: "bg-rose-500 text-rose-50 border-rose-400",
  "": "bg-slate-50 text-slate-400 border-slate-200 hover:border-orange-400 hover:text-slate-500",
};
const CELL_LABEL = { available: "✓", maybe: "?", out: "✕", "": "·" };

export function RosterGrid({ players, weeks, availability, isCaptain, onTapCell, onEditWeek, onEditPlayer, onOpenPlayer }) {
  if (players.length === 0 && weeks.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="roster-empty">
        Nothing here yet. The admin can add players and game weeks.
      </div>
    );
  }
  return (
    <div className="fade-up overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white" data-testid="roster-grid">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="sticky left-0 z-10 min-w-[7.5rem] bg-white px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Player
            </th>
            {weeks.map((w) => (
              <th key={w.id} className={`min-w-[3.9rem] px-1 py-2 text-center ${w.locked ? "opacity-60" : ""}`} data-testid={`week-header-${w.id}`}>
                <div className="flex flex-col items-center gap-0.5">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-800">
                    {w.locked && <Lock className="h-3 w-3 text-slate-500" data-testid={`week-lock-${w.id}`} />}
                    {fmtDate(w.date)}
                  </span>
                  <span className="max-w-[5.5rem] truncate text-[10px] font-medium text-orange-600">{w.opponent || "—"}</span>
                  {w.location ? <span className="max-w-[5.5rem] truncate text-[9px] text-slate-500">@ {w.location}</span> : null}
                  <span className="text-[9px] text-slate-500">{w.time || ""}</span>
                  {isCaptain && (
                    <button
                      data-testid={`week-edit-${w.id}`}
                      onClick={() => onEditWeek(w)}
                      className="mt-1 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-orange-600"
                      aria-label="Edit week"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.id} className="border-b border-slate-100 last:border-0" data-testid={`player-row-${p.id}`}>
              <td className="sticky left-0 z-10 bg-white px-3 py-1">
                <div className="flex items-center justify-between gap-2">
                  <button
                    data-testid={`player-name-${p.id}`}
                    onClick={() => onOpenPlayer(p)}
                    className="rounded px-1 py-0.5 text-left text-sm font-medium text-slate-900 underline-offset-2 transition-colors hover:text-orange-600 hover:underline"
                  >
                    {p.name}
                  </button>
                  <span className="inline-flex items-center gap-2">
                    {isCaptain && (
                      <span className="rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-500" data-testid={`gp-${p.id}`}>
                        GP {p.gp} · AV {p.ga ?? 0}
                      </span>
                    )}
                    {isCaptain && (
                      <button
                        data-testid={`player-edit-${p.id}`}
                        onClick={() => onEditPlayer(p)}
                        className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-orange-600"
                        aria-label="Edit player"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </div>
              </td>
              {weeks.map((w) => {
                const status = availability[`${p.id}:${w.id}`] || "";
                const disabled = w.locked && !isCaptain;
                return (
                  <td key={w.id} className="px-2 py-2">
                    <button
                      data-testid={`cell-${p.id}-${w.id}`}
                      disabled={disabled}
                      onClick={() => onTapCell(p.id, w.id)}
                      className={`cell-btn h-8 w-full rounded-md border text-sm font-bold ${CELL_STYLES[status]} ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
                    >
                      {CELL_LABEL[status]}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
